import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Alert, Box, CircularProgress, Divider, List, Pagination, ThemeProvider, Typography } from "@mui/material";
import useCatalogSearch from "../hooks/useCatalogSearch";
import { CatalogListItem } from "../components/CatalogListItem";
import { useSelector } from "react-redux";
import type { RootState } from "../store";
import Theme from "../utils/theme";
import { scrollbarCss } from "../utils/themeScrollbar";
import CustomToggleButton from "../components/default/CustomToggleButton";

type filterType = "user" | "recipe" | "post";

// *** Constants *** //
const DEFAULT_LIMIT = 10;
const FILTERS: filterType[] = ["user", "recipe", "post"];

// ***** Main Component ***** //
const SearchResultsPage: React.FC = () => {
    // *** Search Variables *** //
    const [params, setParams] = useSearchParams();
    const q = params.get("q") ?? "";
    const visibility = params.get("visibility") ?? undefined;
    const page = Math.max(0, parseInt(params.get("page") ?? "0", 10) || 0);
    const limit = Math.max(1, parseInt(params.get("limit") ?? String(DEFAULT_LIMIT), 10) || DEFAULT_LIMIT);

    // *** Root States *** //
    const authState = useSelector((state: RootState) => state.auth);
    const currentUser = authState.account ? authState.account.username : null;
    const themeState = useSelector((state: RootState) => state.theme);
    const theme = useMemo(() => Theme(themeState.mode), [themeState.mode]);

    // *** Component States *** //
    const [filterSwitch, setFilterSwitch] = useState<filterType[]>(["user", "recipe", "post"]);

    const { data, hasMore, loading, error } = useCatalogSearch({ q, types: filterSwitch, visibility, page, limit, currentUser });

    const handleFilterChange = useCallback((item: filterType) => {
        setFilterSwitch((prev: filterType[]) => {
            if (prev.includes(item)) {
                return prev.filter((prevItem) => item !== prevItem);
            }

            return [...prev, item];
        });
    }, []);

    const totalPages = useMemo(() => {
        return page + 1 + (!loading && hasMore ? 1 : 0);
    }, [page, hasMore, loading]);

    const handlePageChange = useCallback(
        (_: unknown, muiPage: number) => {
            params.set("page", String(muiPage - 1));
            setParams(params, { replace: false });
        },
        [params, setParams]
    );

    return (
        <ThemeProvider theme={theme}>
            <Box p={2}>
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Typography variant="h5" gutterBottom sx={{ flexGrow: 1 }}>
                        Search results{q ? ` for “${q}”` : ""}
                    </Typography>

                    <Box sx={{ display: { xs: "none", sm: "flex" }, justifyContent: "center" }}>
                        <Pagination
                            page={page + 1}
                            onChange={handlePageChange}
                            count={totalPages}
                            variant="outlined"
                            shape="rounded"
                            siblingCount={1}
                            boundaryCount={0}
                        />
                    </Box>
                </Box>
                <Divider />

                <Box sx={{ my: 3 }}>
                    {FILTERS.map((item: filterType) => (
                        <CustomToggleButton
                            key={item}
                            value={item}
                            selected={filterSwitch.includes(item)}
                            onClick={() => {
                                if (item === "user" || item === "recipe" || item === "post") {
                                    handleFilterChange(item);
                                }
                            }}
                            sx={{ mx: 1, maxWidth: "22vw", height: "28px" }}
                        >
                            <Typography variant="body2">{item}</Typography>
                        </CustomToggleButton>
                    ))}
                </Box>

                {loading && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CircularProgress size={20} /> <Typography>Searching…</Typography>
                    </Box>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error.message}
                    </Alert>
                )}

                {!loading && data?.results?.length === 0 && <Typography>No results found.</Typography>}

                {!loading && !!data?.results?.length && (
                    <>
                        <List disablePadding>
                            {data.results.map((item) => (
                                <CatalogListItem key={item.id} item={item} />
                            ))}
                        </List>

                        <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
                            <Pagination page={page + 1} onChange={handlePageChange} count={totalPages} variant="outlined" shape="rounded" />
                        </Box>
                    </>
                )}
            </Box>
        </ThemeProvider>
    );
};

export default SearchResultsPage;
